import {
    BaseChartConfigHelper,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/base-chart-config-helper';
import { CHART_TYPE, DisplayInvalidDataPointAs } from '@tencent/chart/lib/chart-ui/interface';
import { STGrouping } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import {
    DataLabel,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/data-label';
import { labelPositionTypeMap } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/map';
import { filterOriginStyle, generateCustomStyles, NO_SUPPORT_FILL_SYMBOL } from '../utils/style';
import { ChartUILabelPosition, CustomStyles, ShapeOriginStyle } from '../interface/chart-ui.interface';
import type {
    IChartManualLayout,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import type {
    IDataLabelProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-label/data-label.interface';
import type { ChartUIDataItem, ShapeStyle } from '../interface/chart-ui.interface';
import type {
    IAreaChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/area-chart.interface';
import type { GeometryType, IChartAdjust, IChartRendererConfig, IGeometryOptions } from '@tencent/chart/lib/chart-ui/interface';

export class AreaChartConfigHelper extends BaseChartConfigHelper {
    public get type() {
        return CHART_TYPE.AREA_CHART;
    }

    protected get subChart(): IAreaChartProperty {
        return this.context.getSubChartModel().getSubChartProperty() as IAreaChartProperty;
    }

    public getRenderConfig(): IChartRendererConfig {
        const baseConfig = super.getRenderConfig();

        return {
            ...baseConfig,
            displayInvalidDataPointAs: DisplayInvalidDataPointAs.Gap,
            showSinglePoint: false,
            geometryConfig: this.getGeometryConfig(),
        };
    }

    protected getGeometryStyle(chartDataItem: ChartUIDataItem, geomType?: GeometryType): ShapeStyle {
        const { border } = chartDataItem || {};

        return {
            lineWidth: border?.lineWidth,
            lineDash: border?.lineDash,
            stroke: border?.stroke,
            lineJoin: border?.lineJoin,
            lineCap: border?.lineCap,
            customStyles: this.getGeometryCustomStyles(chartDataItem),
        };
    }

    protected getSeriesColor(): IChartRendererConfig['seriesColor'] {
        return this.series.map(ser => (ser.fill === NO_SUPPORT_FILL_SYMBOL ? '' : (ser.fill || '')));
    }

    protected getGeometryOptions(): IGeometryOptions {
        return {
            geometryStyles: this.getGeometryStyles(),
        };
    }

    protected getGeometryStyles() {
        return {
            area: this.createIdMappingCfg((chartDataItem) => {
                const { fill, border } = chartDataItem || {};
                return this.filterShapeAttr({
                    fill,
                    fillOpacity: 1,
                    // g2多图形绘制，按照图形进行进行分层
                    // 如果绘制完area再绘制line，会导致重叠部分下层的线条显示
                    // 如果绘制完line在绘制area，会导致重叠部分上层的线条不显示
                    // 所以这里改成在面积图形上添加stroke
                    lineWidth: border?.lineWidth,
                    lineDash: border?.lineDash,
                    stroke: border?.stroke,
                    lineJoin: border?.lineJoin,
                    lineCap: border?.lineCap,
                    customStyles: this.getAreaCustomStyles(chartDataItem),
                });
            }),
        };
    }

    protected getAdjustsConfig(): IChartAdjust[] {
        const { grouping } = this.subChart;

        if (grouping === STGrouping.stacked) {
            return [{
                type: 'stack',
                reverseOrder: false,
                stackPositiveAndNegativeSeparately: false,
            }];
        }

        if (grouping === STGrouping.percentStacked) {
            return [{
                type: 'stack',
                reverseOrder: false,
                isPercent: true,
                stackPositiveAndNegativeSeparately: false,
            }];
        }

        return [];
    }

    // 超出坐标系范围进行裁剪时，是否需要在垂直方向预留间距，防止折线图折线再画布顶部时被裁显示不全等场景
    protected getLimitInPlotAppendMargin() {
        return false;
    }

    protected getDataLabel(label: IDataLabelProperty): DataLabel {
        return new AreaDataLabel(label, this.context);
    }

    private getAreaCustomStyles(chartDataItem: ChartUIDataItem): CustomStyles {
        return generateCustomStyles(chartDataItem);
    }

    private getGeometryConfig() {
        const { grouping } = this.subChart;
        if (grouping === STGrouping.percentStacked) {
            return {
                zIndexReversed: true,  // 改变渲染顺序
            };
        }
    }
}

class AreaDataLabel extends DataLabel {
    protected getLabelPosition(value: number, dataPointLabel: IDataLabelProperty, layout?: IChartManualLayout) {
        return dataPointLabel.labelPosition
            ? labelPositionTypeMap[dataPointLabel.labelPosition] : ChartUILabelPosition.Center;
    }
}
